import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil } from 'lucide-react'

import { ApiError, api } from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Toast, ToastDescription, ToastTitle } from '@/components/ui/toast'
import { AiProviderSettings } from '@/components/AiProviderSettings'
import { SettingEditor, type SettingEditorSubmit } from '@/components/SettingEditor'
import { groupByCategory, getSettingMeta, type SettingMeta } from '@/lib/settings-categories'
import { formatSettingDisplay } from '@/lib/settings-display'
import { useAuthStore } from '@/stores/auth'
import type {
  SystemSetting,
  SystemSettingListResponse,
  SystemSettingResponse,
} from '@sdr-agend/admin-schemas'

interface ToastState {
  open: boolean
  variant: 'default' | 'destructive'
  title: string
  description?: string
}

/**
 * `/settings` — admin-only CRUD over `system_settings` (Story 3.16).
 *
 * Decisions:
 *   - List groups by category (mapping in `settings-categories.ts`).
 *   - Edit-in-dialog with diff + mandatory rationale (audit traceability).
 *   - RBAC: only `admin` sees the Edit button; other roles see read-only.
 *   - Internal keys (e.g. `seller_notification_templates`) are filtered out
 *     because they aren't in the documented editable set.
 */
export function SettingsPage(): React.ReactElement {
  const role = useAuthStore((s) => s.user?.role)
  const canEdit = role === 'admin'
  const queryClient = useQueryClient()

  const [editing, setEditing] = useState<{ key: string; meta: SettingMeta; current: unknown } | null>(
    null,
  )
  const [serverError, setServerError] = useState<string | null>(null)
  const [toast, setToast] = useState<ToastState>({ open: false, variant: 'default', title: '' })

  const { data, isLoading, isError } = useQuery<SystemSettingListResponse>({
    queryKey: ['admin-system-settings'],
    queryFn: async () => api<SystemSettingListResponse>('/admin/system-settings'),
    enabled: canEdit || role !== undefined,
  })

  const grouped = useMemo(
    () => (data === undefined ? [] : groupByCategory(data.settings)),
    [data],
  )

  function notify(state: Omit<ToastState, 'open'>): void {
    setToast({ open: true, ...state })
  }

  const updateMutation = useMutation({
    mutationFn: async (input: { key: string; payload: SettingEditorSubmit }) =>
      api<SystemSettingResponse>(`/admin/system-settings/${input.key}`, {
        method: 'PATCH',
        body: JSON.stringify(input.payload),
      }),
    onSuccess: (res) => {
      notify({
        variant: 'default',
        title: 'Configuração atualizada',
        description: `\`${res.setting.key}\` salvo com sucesso.`,
      })
      setEditing(null)
      setServerError(null)
      void queryClient.invalidateQueries({ queryKey: ['admin-system-settings'] })
    },
    onError: (err: Error) => {
      const apiErr = err instanceof ApiError ? err : null
      const body = apiErr?.bodyJson as { error?: { message?: string } } | null
      setServerError(body?.error?.message ?? err.message)
    },
  })

  // Roles ≠ admin: render a read-only banner.
  if (role !== undefined && role !== 'admin') {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground">
            Configurações operacionais do produto.
          </p>
        </div>
        <Card className="border-amber-500/40 bg-amber-50/40 p-4 dark:bg-amber-950/20">
          <div className="flex flex-wrap items-center gap-1 text-sm">
            <span>Acesso somente leitura. Edição restrita ao papel</span>
            <strong>admin</strong>
            <span>. Você</span>
            <span>(</span>
            <Badge variant="secondary">{role}</Badge>
            <span>) pode visualizar mas não modificar.</span>
          </div>
        </Card>
        <SettingsList grouped={grouped} canEdit={false} onEdit={() => undefined} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground">
            Configurações operacionais do produto. Toda alteração é auditada.
          </p>
        </div>
      </div>

      {isLoading && (
        <p className="text-sm text-muted-foreground">Carregando configurações…</p>
      )}
      {isError && (
        <p className="text-sm text-destructive">Erro ao carregar configurações.</p>
      )}

      {!isLoading && !isError && <AiProviderSettings settings={data?.settings ?? []} />}

      {!isLoading && !isError && (
        <SettingsList
          grouped={grouped}
          canEdit={canEdit}
          onEdit={(setting) => {
            const meta = getSettingMeta(setting.key)
            if (meta === undefined) return
            setServerError(null)
            setEditing({ key: setting.key, meta, current: setting.value })
          }}
        />
      )}

      {editing !== null && (
        <SettingEditor
          open={editing !== null}
          onOpenChange={(open) => {
            if (!open) {
              setEditing(null)
              setServerError(null)
            }
          }}
          meta={editing.meta}
          currentValue={editing.current}
          submitting={updateMutation.isPending}
          serverError={serverError}
          onSubmit={(payload) => {
            updateMutation.mutate({ key: editing.key, payload })
          }}
        />
      )}

      <Toast
        open={toast.open}
        onOpenChange={(open) => setToast((s) => ({ ...s, open }))}
        variant={toast.variant}
      >
        <div className="grid gap-1">
          <ToastTitle>{toast.title}</ToastTitle>
          {toast.description !== undefined && (
            <ToastDescription>{toast.description}</ToastDescription>
          )}
        </div>
      </Toast>
    </div>
  )
}

interface SettingsListProps {
  grouped: ReturnType<typeof groupByCategory<SystemSetting>>
  canEdit: boolean
  onEdit: (setting: SystemSetting) => void
}

function SettingsList({ grouped, canEdit, onEdit }: SettingsListProps): React.ReactElement {
  if (grouped.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Nenhuma configuração editável disponível.</p>
    )
  }
  return (
    <div className="space-y-6">
      {grouped.map((group) => (
        <Card
          key={group.category}
          className="p-4"
          role="region"
          aria-label={group.label}
        >
          <h2 className="mb-3 text-base font-semibold">{group.label}</h2>
          <div className="divide-y">
            {group.items.map((item) => (
              <div
                key={item.key}
                className="flex flex-wrap items-start justify-between gap-4 py-3"
                data-testid={`setting-row-${item.key}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{item.meta.label}</span>
                    <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-muted-foreground">
                      {item.key}
                    </code>
                  </div>
                  {item.description !== null && (
                    <p className="mt-1 text-xs text-muted-foreground">{item.description}</p>
                  )}
                  <div className="mt-2 rounded bg-muted/40 p-3" data-testid={`setting-display-${item.key}`}>
                    {formatSettingDisplay(item.key, item.value)}
                  </div>
                </div>
                {canEdit && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onEdit(item)}
                    aria-label={`Editar ${item.meta.label}`}
                  >
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                    Editar
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  )
}

export default SettingsPage
